import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Radio,
  Box,
  Typography,
  CircularProgress,
  Pagination,
  Divider
} from '@mui/material';
import React, { useState } from 'react';
import {
  NotificationSetupTemplate,
  PaginatedNotificationSetupTemplateDTO
} from '../../../services/Hub/notificationCenter/types';

const getTagColor = (
  tagText: string
): 'primary' | 'secondary' | 'success' | 'warning' | 'error' => {
  const colors: ('primary' | 'secondary' | 'success' | 'warning' | 'error')[] = [
    'primary',
    'secondary',
    'success',
    'warning',
    'error'
  ];

  let hash = 0;
  for (let i = 0; i < tagText.length; i++) {
    const char = tagText.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  const colorIndex = Math.abs(hash) % colors.length;
  return colors[colorIndex];
};

interface SetupTemplatesModalProps {
  open: boolean;
  onClose: () => void;
  setupTemplatesData: PaginatedNotificationSetupTemplateDTO;
  onSelectTemplate: (template: NotificationSetupTemplate) => void;
  onLoadPage: (page: number, itemsPerPage: number) => void;
  isLoading?: boolean;
  isCreating?: boolean;
}

// Componente para a lista de templates
const TemplatesList = ({
  setupTemplatesData,
  selectedTemplate,
  onTemplateSelect,
  onLoadPage,
  isLoading
}: {
  setupTemplatesData: PaginatedNotificationSetupTemplateDTO;
  selectedTemplate: NotificationSetupTemplate | null;
  onTemplateSelect: (template: NotificationSetupTemplate) => void;
  onLoadPage: (page: number, itemsPerPage: number) => void;
  isLoading: boolean;
}) => {
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    onLoadPage(page, setupTemplatesData.itemsPerPage);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Templates Disponíveis
      </Typography>
      <List dense sx={{ border: '1px solid #e9ecef', width: '600px', borderRadius: 2 }}>
        {setupTemplatesData.items.map((template: NotificationSetupTemplate) => (
          <React.Fragment key={template.id}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => onTemplateSelect(template)}
                selected={selectedTemplate?.id === template.id}
                sx={{
                  py: 0.05,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    '&:hover': {
                      backgroundColor: 'primary.light'
                    }
                  }
                }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Radio
                    checked={selectedTemplate?.id === template.id}
                    onChange={() => onTemplateSelect(template)}
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body1" fontWeight="medium">
                        {template.name.replace(/\[([^\]]+)\]/g, '').trim()}
                      </Typography>
                      {template.name.match(/\[([^\]]+)\]/g)?.map((tag, index) => {
                        const tagText = tag.replace(/[\[\]]/g, '');
                        return (
                          <Chip
                            key={index}
                            label={tagText}
                            size="small"
                            color={getTagColor(tagText)}
                            variant="outlined"
                          />
                        );
                      })}
                    </Box>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {template.source}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <Divider />
          </React.Fragment>
        ))}
      </List>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          count={Math.ceil(setupTemplatesData.total / setupTemplatesData.itemsPerPage)}
          page={setupTemplatesData.currentPage}
          onChange={handlePageChange}
          color="primary"
          size="small"
        />
      </Box>
    </Box>
  );
};

export default function SetupTemplatesModal({
  open,
  onClose,
  setupTemplatesData,
  onSelectTemplate,
  onLoadPage,
  isLoading = false,
  isCreating = false
}: SetupTemplatesModalProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationSetupTemplate | null>(null);

  const handleTemplateSelect = (template: NotificationSetupTemplate) => {
    setSelectedTemplate(template);
  };

  const handleCreateTrigger = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate);
    }
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      fullWidth>
      <DialogTitle>Criar Trigger a partir do Template</DialogTitle>
      <DialogContent sx={{ height: '620px', overflow: 'auto' }}>
        <TemplatesList
          setupTemplatesData={setupTemplatesData}
          selectedTemplate={selectedTemplate}
          onTemplateSelect={handleTemplateSelect}
          onLoadPage={onLoadPage}
          isLoading={isLoading}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isCreating}>
          Cancelar
        </Button>
        <Button
          onClick={handleCreateTrigger}
          variant="contained"
          disabled={!selectedTemplate || isCreating}
          startIcon={isCreating ? <CircularProgress size={16} /> : undefined}>
          {isCreating ? 'Criando...' : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
